import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'student'
  });

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchUsers();
    } else if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      
      alert(language === 'tr' ? '✅ Rol güncellendi!' : '✅ Role updated!');
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      alert(language === 'tr' ? '❌ Rol güncellenemedi!' : '❌ Failed to update role!');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === 'tr' ? 'Bu kullanıcıyı silmek istediğinizden emin misiniz?' : 'Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        withCredentials: true
      });
      
      alert(language === 'tr' ? '✅ Kullanıcı silindi!' : '✅ User deleted!');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(language === 'tr' ? '❌ Kullanıcı silinemedi!' : '❌ Failed to delete user!');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/admin/users`,
        newUser,
        { withCredentials: true }
      );
      
      alert(language === 'tr' ? '✅ Kullanıcı eklendi!' : '✅ User added!');
      setShowAddModal(false);
      setNewUser({ email: '', name: '', role: 'student' });
      fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      alert(language === 'tr' ? '❌ Kullanıcı eklenemedi!' : '❌ Failed to add user!');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'admin': 'bg-red-100 text-red-800',
      'teacher': 'bg-purple-100 text-purple-800',
      'student': 'bg-blue-100 text-blue-800',
      'parent': 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleIcon = (role) => {
    const icons = {
      'admin': '👑',
      'teacher': '👨‍🏫',
      'student': '🎓',
      'parent': '👨‍👩‍👧'
    };
    return icons[role] || '👤';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Kullanıcı Yönetimi' : 'User Management'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              👥 {language === 'tr' ? 'Kullanıcı Yönetimi' : 'User Management'}
            </h1>
            <p className="text-gray-600 mt-2">
              {language === 'tr' 
                ? 'Kullanıcıları görüntüle, düzenle ve yönet'
                : 'View, edit, and manage users'}
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-300 font-semibold"
          >
            + {language === 'tr' ? 'Kullanıcı Ekle' : 'Add User'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Toplam' : 'Total'}</div>
            <div className="text-3xl font-bold text-gray-800">{users.length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Adminler' : 'Admins'}</div>
            <div className="text-3xl font-bold text-red-600">{users.filter(u => u.role === 'admin').length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Öğretmenler' : 'Teachers'}</div>
            <div className="text-3xl font-bold text-purple-600">{users.filter(u => u.role === 'teacher').length}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Öğrenciler' : 'Students'}</div>
            <div className="text-3xl font-bold text-blue-600">{users.filter(u => u.role === 'student').length}</div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {language === 'tr' ? 'Kullanıcı' : 'User'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {language === 'tr' ? 'Email' : 'Email'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {language === 'tr' ? 'Rol' : 'Role'}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                    {language === 'tr' ? 'Kayıt Tarihi' : 'Created'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">
                    {language === 'tr' ? 'İşlemler' : 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id || u.email} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">{getRoleIcon(u.role)}</div>
                        <div className="font-medium text-gray-800">{u.name || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{u.email}</td>
                    <td className="px-6 py-4">
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id || u.email, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${getRoleBadgeColor(u.role)}`}
                        disabled={u.email === 'kayhankarli@gmail.com'}
                      >
                        <option value="admin">Admin</option>
                        <option value="teacher">Teacher</option>
                        <option value="student">Student</option>
                        <option value="parent">Parent</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {u.email !== 'kayhankarli@gmail.com' && (
                        <button
                          onClick={() => handleDeleteUser(u.id || u.email)}
                          className="text-red-600 hover:text-red-800 font-semibold text-sm"
                        >
                          {language === 'tr' ? 'Sil' : 'Delete'}
                        </button>
                      )}
                      {u.email === 'kayhankarli@gmail.com' && (
                        <span className="text-yellow-600 font-semibold text-sm">
                          {language === 'tr' ? 'Korumalı' : 'Protected'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {users.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">{language === 'tr' ? 'Henüz kullanıcı yok' : 'No users yet'}</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {language === 'tr' ? 'Yeni Kullanıcı Ekle' : 'Add New User'}
            </h2>
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Email' : 'Email'}
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Ad Soyad' : 'Full Name'}
                </label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Rol' : 'Role'}
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="parent">Parent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewUser({ email: '', name: '', role: 'student' });
                  }}
                  className="flex-1 bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  {language === 'tr' ? 'İptal' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  {language === 'tr' ? 'Ekle' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
